import React, { createContext, useContext, useState, useEffect } from 'react';
import type { ReactNode } from 'react';
import type { AuthContextType, AuthState, AuthUser, AuthRole } from '../types/auth';
import { loginWithPassword, verifyToken as verifyTokenApi } from '../services/authApi';
import {
  storeToken,
  getStoredToken,
  removeToken,
  parseJwt,
  isTokenExpired
} from '../utils/auth';

// Create the context
const AuthContext = createContext<AuthContextType | null>(null);

// Provider props interface
interface AuthProviderProps {
  children: ReactNode;
}

/**
 * Authentication Provider Component
 * Manages authentication state and provides auth methods to the app
 */
export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [state, setState] = useState<AuthState>({
    isAuthenticated: false,
    token: null,
    user: null,
    isLoading: true,
  });

  // Check for stored token on mount
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const storedToken = getStoredToken();

        if (!storedToken) {
          setState((prev) => ({ ...prev, isLoading: false }));
          return;
        }

        // Check if token is expired client-side first
        if (isTokenExpired(storedToken)) {
          removeToken();
          setState((prev) => ({ ...prev, isLoading: false }));
          return;
        }

        // Verify token with server
        const result = await verifyTokenApi(storedToken);

        if (result.valid && result.decoded) {
          setState({
            isAuthenticated: true,
            token: storedToken,
            user: result.decoded,
            isLoading: false,
          });
        } else {
          removeToken();
          setState((prev) => ({ ...prev, isLoading: false }));
        }
      } catch (error) {
        console.error('Auth check failed:', error);
        removeToken();
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    };

    checkAuth();
  }, []);

  /**
   * Login with a username and password
   * @param username - Account username
   * @param password - Account password
   * @param role - Credential set to authenticate against (defaults to 'client')
   * @throws AuthError if login fails
   */
  const login = async (
    username: string,
    password: string,
    role: AuthRole = 'client'
  ): Promise<void> => {
    try {
      const response = await loginWithPassword(username, password, role);

      // Store token
      storeToken(response.token);

      // Parse user data from token
      const userData = parseJwt(response.token);

      // Update state
      setState({
        isAuthenticated: true,
        token: response.token,
        user: userData as AuthUser,
        isLoading: false,
      });
    } catch (error) {
      console.error('Login failed:', error);
      throw error;
    }
  };

  /**
   * Logout user
   */
  const logout = (): void => {
    // Clear token from storage
    removeToken();

    // Reset state
    setState({
      isAuthenticated: false,
      token: null,
      user: null,
      isLoading: false,
    });
  };

  /**
   * Verify current token
   */
  const verifyToken = async (): Promise<void> => {
    if (!state.token) {
      return;
    }

    try {
      const result = await verifyTokenApi(state.token);

      if (!result.valid) {
        logout();
      }
    } catch (error) {
      console.error('Token verification failed:', error);
      logout();
    }
  };

  const contextValue: AuthContextType = {
    ...state,
    login,
    logout,
    verifyToken,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

/**
 * Custom hook to use auth context
 * @returns AuthContext value
 * @throws Error if used outside of AuthProvider
 */
export const useAuthContext = (): AuthContextType => {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }

  return context;
};

export default AuthContext;
